import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import type { Category, Color, FaqItem, Media, Product, SiteSettings, StockType, Video } from '@nhanho/shared'
import { STOCK_LABEL, slugifyVi, formatVND, computeSalePrice, isDiscountActive, mediaSizeHint } from '@nhanho/shared'
import { products as api, categories as catApi, colors as colorApi, videos as videoApi, settings as settingsApi, explainError, type ProductBody } from '../../lib/api'
import { useToast } from '../../lib/toast'
import { useDirtyGuard } from '../../lib/dirty'
import { PageHeader } from '../../components/ui/PageHeader'
import { Button } from '../../components/ui/Button'
import { Card } from '../../components/ui/Card'
import { Field } from '../../components/ui/Field'
import { Input, MoneyInput, Select, Textarea } from '../../components/ui/Input'
import { Toggle } from '../../components/ui/Toggle'
import { TagInput } from '../../components/ui/TagInput'
import { Spinner } from '../../components/ui/Spinner'
import { Badge } from '../../components/ui/Badge'
import { ConfirmDialog } from '../../components/ui/Modal'
import { MediaPicker } from '../../components/media/MediaPicker'
import { SortableImages } from '../../components/media/SortableImages'
import { BadgeMask } from '../../components/product/BadgeMask'
import { RichTextEditor } from '../../components/editor/RichTextEditor'
import { SeoPanel, EMPTY_SEO, seoFromMeta, seoToMeta, type SeoValue } from '../../components/seo/SeoPanel'
import { FaqEditor } from '../../components/seo/FaqEditor'
import { VariantsEditor, newVariant, LOW_STOCK, type VariantRow } from './VariantsEditor'

type Tab = 'basic' | 'media' | 'variants' | 'seo'

/**
 * Thông số. Ô số để `null` khi trống (KHÔNG phải `0`) — `0` là một giá trị, còn `null` là "chưa
 * điền"; `toBody` gửi `null` xuống để Mongoose xoá được field (bỏ key = giữ giá trị cũ).
 */
interface SpecsForm {
  lengthCm: number | null; widthCm: number | null; heightCm: number | null; weightG: number | null
  materials: string[]; technologies: string[]; accessories: string[]
}

const EMPTY_SPECS: SpecsForm = {
  lengthCm: null, widthCm: null, heightCm: null, weightG: null,
  materials: [], technologies: [], accessories: [],
}

interface Form {
  title: string; slug: string; slugTouched: boolean; sku: string; description: string; shortDescription: string
  category: string; tags: string[]; price: number | null; compareAtPrice: number | null
  discount: { enabled: boolean; percent: number | null; startsAt: string; endsAt: string }
  stockType: StockType; stockQuantity: number | null
  preOrder: { daysUntilAvailable: number | null; estimatedDate: string; note: string }
  images: Media[]; videos: string[]; colors: string[]; variants: VariantRow[]
  shopeeUrl: string; zaloLink: string
  published: boolean; featured: boolean; isHot: boolean; isNewArrival: boolean; order: number
  brand: string; faq: FaqItem[]; seo: SeoValue
  specs: SpecsForm
}

const EMPTY: Form = {
  title: '', slug: '', slugTouched: false, sku: '', description: '', shortDescription: '', category: '', tags: [],
  price: null, compareAtPrice: null,
  discount: { enabled: false, percent: null, startsAt: '', endsAt: '' },
  stockType: 'in_stock', stockQuantity: null,
  preOrder: { daysUntilAvailable: null, estimatedDate: '', note: '' },
  images: [], videos: [], colors: [], variants: [], shopeeUrl: '', zaloLink: '',
  published: false, featured: false, isHot: false, isNewArrival: false, order: 0,
  brand: '', faq: [], seo: EMPTY_SEO, specs: EMPTY_SPECS,
}

const refId = (v: unknown): string => (v && typeof v === 'object' && 'id' in v ? String((v as { id: string }).id) : typeof v === 'string' ? v : '')
/** ISO → `YYYY-MM-DD` theo giờ máy (input type=date). */
const toDateInput = (iso: string | null | undefined): string => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}
/** `YYYY-MM-DD` → ISO ở đầu/cuối ngày theo giờ máy — "đến 10/09" nghĩa là hết ngày 10/09. */
const fromDateInput = (v: string, edge: 'start' | 'end'): string | null =>
  v ? new Date(`${v}T${edge === 'start' ? '00:00:00' : '23:59:59'}`).toISOString() : null

/**
 * Ô số của thông số: `''` → `null` ("chưa điền"), số âm/không hợp lệ → `null`.
 * `null` chứ không `0` — `0 g` là một lời khẳng định, còn để trống là không nói gì.
 */
const toMeasure = (v: string): number | null => {
  const n = Number(v)
  return v.trim() === '' || !Number.isFinite(n) || n < 0 ? null : n
}

const fromDoc = (p: Product): Form => ({
  title: p.title, slug: p.slug, slugTouched: true, sku: p.sku ?? '', description: p.description, shortDescription: p.shortDescription ?? '',
  category: refId(p.category), tags: p.tags ?? [], price: p.price, compareAtPrice: p.compareAtPrice ?? null,
  discount: p.discount
    ? { enabled: true, percent: p.discount.percent, startsAt: toDateInput(p.discount.startsAt), endsAt: toDateInput(p.discount.endsAt) }
    : { enabled: false, percent: null, startsAt: '', endsAt: '' },
  stockType: p.stockType, stockQuantity: p.stockQuantity ?? null,
  preOrder: { daysUntilAvailable: p.preOrder?.daysUntilAvailable ?? null, estimatedDate: toDateInput(p.preOrder?.estimatedDate), note: p.preOrder?.note ?? '' },
  images: (p.images ?? []).filter((x): x is Media => typeof x === 'object'),
  videos: (p.videos ?? []).map(refId), colors: (p.colors ?? []).map(refId),
  // ⚠️ `key` phải **duy nhất mỗi dòng**: `update`/`remove` của `VariantsEditor` so bằng nó, nên hai
  // dòng trùng key là sửa/xoá một dòng ăn vào cả hai. API từng quên trả `id` cho biến thể (13/10) →
  // mọi dòng cùng `undefined` → cả bảng dính nhau. Đã sửa ở `Product.ts`; `??` là lưới thứ hai.
  variants: (p.variants ?? []).map((v) => ({
    key: v.id ?? crypto.randomUUID(), id: v.id, sku: v.sku, label: v.label ?? '', color: refId(v.color), size: v.size ?? '', price: v.price, compareAtPrice: v.compareAtPrice ?? null,
    stockQuantity: v.stockQuantity ?? 0, image: typeof v.image === 'object' ? (v.image as Media | null) : null, active: v.active,
  })),
  shopeeUrl: p.shopeeUrl ?? '', zaloLink: p.zaloLink ?? '',
  published: p.published, featured: p.featured ?? false, isHot: p.isHot ?? false, isNewArrival: p.isNewArrival ?? false, order: p.order ?? 0,
  brand: p.brand ?? '', faq: (p.faq ?? []).map((f) => ({ question: f.question, answer: f.answer })),
  seo: seoFromMeta(p.meta),
  specs: {
    lengthCm: p.specs?.lengthCm ?? null, widthCm: p.specs?.widthCm ?? null, heightCm: p.specs?.heightCm ?? null,
    weightG: p.specs?.weightG ?? null,
    materials: p.specs?.materials ?? [], technologies: p.specs?.technologies ?? [], accessories: p.specs?.accessories ?? [],
  },
})

/**
 * Form → body gửi API. `slug` chỉ gửi khi user **tự sửa** (`slugTouched`); không sửa thì
 * để server sinh bằng `uniqueSlug()` → hai sản phẩm cùng tên ra `-2` thay vì 409.
 */
const toBody = (f: Form): ProductBody => ({
  title: f.title, slug: f.slugTouched && f.slug ? f.slug : undefined, sku: f.sku, description: f.description, shortDescription: f.shortDescription,
  category: f.category || null, tags: f.tags, price: f.price ?? 0, compareAtPrice: f.compareAtPrice,
  discount: f.discount.enabled && f.discount.percent
    ? { percent: f.discount.percent, startsAt: fromDateInput(f.discount.startsAt, 'start'), endsAt: fromDateInput(f.discount.endsAt, 'end') }
    : null,
  stockType: f.stockType, stockQuantity: f.stockQuantity,
  preOrder: f.stockType === 'pre_order' ? { daysUntilAvailable: f.preOrder.daysUntilAvailable, estimatedDate: fromDateInput(f.preOrder.estimatedDate, 'start'), note: f.preOrder.note } : null,
  images: f.images.map((m) => m.id), videos: f.videos, colors: f.colors,
  variants: f.variants.map((v, i) => ({ id: v.id, sku: v.sku, label: v.label, color: v.color || null, size: v.size, price: v.price ?? 0, compareAtPrice: v.compareAtPrice, stockQuantity: v.stockQuantity ?? 0, image: v.image?.id ?? null, order: i, active: v.active })),
  shopeeUrl: f.shopeeUrl, zaloLink: f.zaloLink,
  published: f.published, featured: f.featured, isHot: f.isHot, isNewArrival: f.isNewArrival, order: f.order,
  brand: f.brand,
  // Bỏ câu hỏi còn trống để không đụng `min(5)` của Zod khi user thêm dòng rồi bỏ đó.
  faq: f.faq.filter((x) => x.question.trim() && x.answer.trim()),
  meta: seoToMeta(f.seo),
  // Gửi nguyên object: ô trống là `null` để xoá được, không bỏ key (xem `ProductBody.specs`).
  specs: f.specs,
})

/** Tab chứa ô lỗi đầu tiên — để nhảy tới đúng chỗ thay vì báo "có ô đỏ" ở tab khác. */
function firstErrorTab(fe: Record<string, string>): Tab | null {
  const keys = Object.keys(fe)
  if (!keys.length) return null
  if (keys.some((k) => k.startsWith('variants'))) return 'variants'
  if (keys.some((k) => k.startsWith('meta') || k.startsWith('faq'))) return 'seo'
  if (keys.some((k) => k === 'images' || k === 'videos')) return 'media'
  return 'basic'
}

export default function ProductForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const [form, setForm] = useState<Form | null>(id ? null : EMPTY)
  /** Bản đã lưu — để tính dirty và biết trạng thái publish thật trên web. */
  const [initial, setInitial] = useState<Form | null>(id ? null : EMPTY)
  const [cats, setCats] = useState<Category[]>([])
  const [allColors, setAllColors] = useState<Color[]>([])
  const [allVideos, setAllVideos] = useState<Video[]>([])
  const [site, setSite] = useState<SiteSettings | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [del, setDel] = useState(false)
  const [confirmSlug, setConfirmSlug] = useState<null | (() => void)>(null)
  const [confirmHide, setConfirmHide] = useState(false)
  const [tab, setTab] = useState<Tab>('basic')
  const [pickImages, setPickImages] = useState(false)

  useEffect(() => {
    Promise.all([catApi.list({ limit: 500, sort: 'name' }), colorApi.list({ limit: 200 }), videoApi.list({ limit: 200 }), settingsApi.get()])
      .then(([c, co, v, s]) => { setCats(c.docs); setAllColors(co.docs); setAllVideos(v.docs); setSite(s) })
      .catch((e) => toast.error(`Không tải được danh mục/màu/video: ${explainError(e).message}`))
  }, [toast])
  useEffect(() => {
    if (!id) return
    api.get(id).then((p) => { const f = fromDoc(p); setForm(f); setInitial(f) }).catch((e) => { toast.error(explainError(e).message); navigate('/products') })
  }, [id, navigate, toast])

  const dirty = useMemo(() => !!form && !!initial && JSON.stringify(toBody(form)) !== JSON.stringify(toBody(initial)), [form, initial])
  useDirtyGuard(dirty)

  if (!form) return <Spinner />
  const patch = (p: Partial<Form>) => setForm((f) => (f ? { ...f, ...p } : f))
  const patchSpecs = (p: Partial<SpecsForm>) => patch({ specs: { ...form.specs, ...p } })
  const setTitle = (title: string) => patch({ title, slug: form.slugTouched ? form.slug : slugifyVi(title) })
  const selectedColors = allColors.filter((c) => form.colors.includes(c.id))

  const discountPreview = form.discount.enabled && form.discount.percent
    ? { percent: form.discount.percent, startsAt: fromDateInput(form.discount.startsAt, 'start'), endsAt: fromDateInput(form.discount.endsAt, 'end') }
    : null
  const onSalePreview = isDiscountActive(discountPreview)
  const salePercent = onSalePreview ? form.discount.percent : null
  const previewBadgeInput = { isHot: form.isHot, isNewArrival: form.isNewArrival, onSale: onSalePreview, discountPercent: salePercent }

  const doSave = async (next: Form) => {
    setBusy(true); setErrors({})
    try {
      const body = toBody(next)
      const saved = id ? await api.update(id, body) : await api.create(body)
      const f = fromDoc(saved)
      setForm(f); setInitial(f)
      toast.success(saved.published ? 'Đã lưu — đang hiển thị trên web' : 'Đã lưu (đang ẩn khỏi web)')
      if (!id) navigate(`/products/${saved.id}`, { replace: true })
    } catch (e) {
      const { message, fieldErrors } = explainError(e)
      setErrors(fieldErrors)
      toast.error(message)
      const t = firstErrorTab(fieldErrors)
      if (t) setTab(t)
    } finally { setBusy(false) }
  }

  /** Đổi slug của sản phẩm đang hiển thị = gãy link cũ → hỏi trước (`CLAUDE.md` §3). */
  const save = (next: Form = form) => {
    if (initial?.published && id && next.slug !== initial.slug) {
      setConfirmSlug(() => () => void doSave(next))
      return
    }
    void doSave(next)
  }

  const TABS: { k: Tab; label: string; badge?: number }[] = [
    { k: 'basic', label: 'Cơ bản' },
    { k: 'media', label: 'Ảnh & Video', badge: form.images.length + form.videos.length || undefined },
    { k: 'variants', label: 'Biến thể', badge: form.variants.length || undefined },
    { k: 'seo', label: 'SEO' },
  ]
  const errCount = Object.keys(errors).length

  return (
    <>
      <PageHeader
        title={id ? 'Sửa sản phẩm' : 'Sản phẩm mới'}
        subtitle={form.slug ? `/san-pham/${form.slug}${!form.slugTouched ? ' (tự sinh; trùng sẽ thêm -2)' : ''}` : undefined}
        actions={<>
          {initial?.published !== undefined && id && (
            <Badge tone={initial.published ? 'success' : 'neutral'}>{initial.published ? 'Đang hiển thị' : 'Đang ẩn'}</Badge>
          )}
          {id && <Button variant="ghost" className="text-danger" onClick={() => setDel(true)}>Xoá</Button>}
          {form.published ? (
            <Button variant="secondary" onClick={() => setConfirmHide(true)} loading={busy}>Gỡ khỏi web</Button>
          ) : (
            <Button variant="secondary" onClick={() => save({ ...form, published: true })} loading={busy}>Lưu & hiển thị</Button>
          )}
          <Button onClick={() => save()} loading={busy}>{dirty ? 'Lưu' : 'Đã lưu'}</Button>
        </>}
      />

      <div className="mb-4 flex gap-1 border-b border-border">
        {TABS.map((t) => (
          <button key={t.k} type="button" onClick={() => setTab(t.k)} className={`-mb-px flex items-center gap-1.5 border-b-2 px-4 py-2 text-sm font-medium ${tab === t.k ? 'border-primary-700 text-primary-950' : 'border-transparent text-ink-muted hover:text-ink'}`}>
            {t.label}{t.badge ? <Badge tone="primary">{t.badge}</Badge> : null}
            {firstErrorTab(errors) === t.k && errCount > 0 && <span className="size-2 rounded-full bg-danger" aria-label="Có lỗi" />}
          </button>
        ))}
      </div>

      {tab === 'basic' && (
        <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
          <div className="space-y-5">
            <Card>
              <div className="space-y-4">
                <Field label="Tên sản phẩm" required error={errors.title}><Input value={form.title} onChange={(e) => setTitle(e.target.value)} className="text-base font-medium" /></Field>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Slug" required hint={initial?.published ? 'Đang hiển thị — đổi slug sẽ tự tạo điều hướng 301 từ link cũ' : '/san-pham/<slug>. Để tự sinh từ tên là an toàn nhất.'} error={errors.slug}>
                    <Input value={form.slug} onChange={(e) => patch({ slug: e.target.value, slugTouched: true })} className="font-mono" />
                  </Field>
                  <Field label="SKU" hint="Mã đối chiếu với Shopee — duy nhất nếu có" error={errors.sku}><Input value={form.sku} onChange={(e) => patch({ sku: e.target.value.toUpperCase() })} className="font-mono uppercase" /></Field>
                </div>
                <Field label="Mô tả ngắn" hint="1–2 câu cho card. Để trống → tự lấy từ mô tả." error={errors.shortDescription}><Textarea value={form.shortDescription} onChange={(e) => patch({ shortDescription: e.target.value })} maxLength={300} className="min-h-16" /></Field>
              </div>
            </Card>
            <Card title="Mô tả chi tiết">
              {errors.description && <p className="mb-2 text-xs text-danger">{errors.description}</p>}
              <RichTextEditor content={form.description} onChange={(html) => patch({ description: html })} resetKey={id} placeholder="Chất liệu, kích thước, cách dùng, lưu ý…" />
            </Card>
            <Card title="Giá & tồn kho">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Giá bán (VND)" required hint={form.variants.length ? 'Có biến thể → giá này chỉ là giá cơ bản' : undefined} error={errors.price}><MoneyInput value={form.price} onChange={(v) => patch({ price: v })} /></Field>
                <Field label="Giá niêm yết (gạch ngang)" hint="Chỉ để hiện giá gốc cao hơn khi KHÔNG chạy giảm giá %" error={errors.compareAtPrice}><MoneyInput value={form.compareAtPrice} onChange={(v) => patch({ compareAtPrice: v })} /></Field>
                <Field label="Tình trạng hàng" required error={errors.stockType}>
                  <div className="flex flex-wrap gap-2">
                    {(Object.keys(STOCK_LABEL) as StockType[]).map((k) => (
                      <label key={k} className={`cursor-pointer rounded-button border px-3 py-2 text-sm ${form.stockType === k ? 'border-primary-700 bg-primary-50 text-primary-950' : 'border-border-strong text-ink-muted'}`}>
                        <input type="radio" name="stockType" className="sr-only" checked={form.stockType === k} onChange={() => patch({ stockType: k })} />{STOCK_LABEL[k]}
                      </label>
                    ))}
                  </div>
                </Field>
                {form.stockType === 'in_stock' && !form.variants.length && (
                  <Field label="Số lượng còn" hint={form.stockQuantity != null && form.stockQuantity <= LOW_STOCK ? `Tồn thấp (≤ ${LOW_STOCK}) — dashboard sẽ cảnh báo` : 'Để trống = không hiện số cụ thể'} error={errors.stockQuantity}>
                    <Input type="number" min={0} value={form.stockQuantity ?? ''} onChange={(e) => patch({ stockQuantity: e.target.value === '' ? null : Math.max(0, Math.floor(Number(e.target.value))) })} />
                  </Field>
                )}
              </div>
              {form.stockType === 'pre_order' && (
                <div className="mt-4 grid gap-4 rounded-card-sm bg-warning-tint p-4 sm:grid-cols-3">
                  <Field label="Số ngày đến khi có hàng" error={errors['preOrder.daysUntilAvailable']}><Input type="number" min={1} max={365} value={form.preOrder.daysUntilAvailable ?? ''} onChange={(e) => patch({ preOrder: { ...form.preOrder, daysUntilAvailable: e.target.value === '' ? null : Number(e.target.value) } })} /></Field>
                  <Field label="Ngày dự kiến" error={errors['preOrder.estimatedDate']}><Input type="date" value={form.preOrder.estimatedDate} onChange={(e) => patch({ preOrder: { ...form.preOrder, estimatedDate: e.target.value } })} /></Field>
                  <Field label="Ghi chú" error={errors['preOrder.note']}><Input value={form.preOrder.note} onChange={(e) => patch({ preOrder: { ...form.preOrder, note: e.target.value } })} placeholder="Đang sản xuất, giao từ 15/8" maxLength={300} /></Field>
                </div>
              )}
            </Card>
            <Card title="Giảm giá">
              <div className="space-y-4">
                <Toggle checked={form.discount.enabled} onChange={(v) => patch({ discount: { ...form.discount, enabled: v, percent: v ? (form.discount.percent ?? 10) : form.discount.percent } })} label="Chạy giảm giá theo %" />
                {form.discount.enabled && (
                  <div className="grid gap-4 rounded-card-sm bg-warning-tint p-4 sm:grid-cols-3">
                    <Field label="Giảm (%)" required error={errors['discount.percent']} hint="1–90. Áp cho giá bán và mọi biến thể.">
                      <Input type="number" min={1} max={90} value={form.discount.percent ?? ''} onChange={(e) => patch({ discount: { ...form.discount, percent: e.target.value === '' ? null : Math.min(90, Math.max(1, Math.floor(Number(e.target.value)))) } })} />
                    </Field>
                    <Field label="Từ ngày" hint="Để trống = ngay bây giờ" error={errors['discount.startsAt']}><Input type="date" value={form.discount.startsAt} onChange={(e) => patch({ discount: { ...form.discount, startsAt: e.target.value } })} /></Field>
                    <Field label="Đến hết ngày" hint="Để trống = vô thời hạn" error={errors['discount.endsAt']}><Input type="date" value={form.discount.endsAt} onChange={(e) => patch({ discount: { ...form.discount, endsAt: e.target.value } })} /></Field>
                    <p className="text-xs text-ink sm:col-span-3">
                      {form.discount.percent && form.price != null ? (
                        onSalePreview
                          ? <>Trên web: <strong>{formatVND(computeSalePrice(form.price, form.discount.percent))}</strong> <s className="text-ink-muted">{formatVND(form.price)}</s> · mask <strong>-{form.discount.percent}%</strong> góc ảnh. Hết hạn tự tắt.</>
                          : <>Giảm giá <strong>chưa/hết hiệu lực</strong> theo lịch trên — web hiện giá thường. Mask chỉ hiện trong thời gian giảm.</>
                      ) : 'Nhập % và giá bán để xem trước.'}
                    </p>
                  </div>
                )}
              </div>
            </Card>
            <Card title="Thông số">
              <div className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  {/* `as="div"`: ba input trong một `<label>` thì click vào nhãn nhảy vào ô "Dài" — xem JSDoc của `Field`. */}
                  <Field
                    label="Kích thước (cm)"
                    as="div"
                    hint="Dài × Rộng × Cao. Phải điền cả 3 ô mới hiện trên web."
                    error={errors['specs.lengthCm'] ?? errors['specs.widthCm'] ?? errors['specs.heightCm']}
                  >
                    <div className="flex items-center gap-1.5">
                      <Input type="number" min={0} step="0.1" aria-label="Dài (cm)" placeholder="Dài" value={form.specs.lengthCm ?? ''} onChange={(e) => patchSpecs({ lengthCm: toMeasure(e.target.value) })} />
                      <span className="text-ink-faint">×</span>
                      <Input type="number" min={0} step="0.1" aria-label="Rộng (cm)" placeholder="Rộng" value={form.specs.widthCm ?? ''} onChange={(e) => patchSpecs({ widthCm: toMeasure(e.target.value) })} />
                      <span className="text-ink-faint">×</span>
                      <Input type="number" min={0} step="0.1" aria-label="Cao (cm)" placeholder="Cao" value={form.specs.heightCm ?? ''} onChange={(e) => patchSpecs({ heightCm: toMeasure(e.target.value) })} />
                    </div>
                  </Field>
                  <Field label="Trọng lượng (g)" hint="Gram. Để trống = không hiện dòng này." error={errors['specs.weightG']}>
                    <Input type="number" min={0} step="1" placeholder="28" value={form.specs.weightG ?? ''} onChange={(e) => patchSpecs({ weightG: toMeasure(e.target.value) })} />
                  </Field>
                </div>
                {/* Ba ô dưới là tag: gõ rồi Enter (hoặc dấu phẩy) thành một thẻ. Nhiều giá trị thì
                    web nối bằng `, ` — "Nhựa PETG, sơn acrylic". */}
                <Field label="Chất liệu" hint="Gõ rồi Enter để thành thẻ. Tối đa 10 thẻ, mỗi thẻ ≤ 60 ký tự." error={errors['specs.materials']}>
                  <TagInput value={form.specs.materials} onChange={(materials) => patchSpecs({ materials })} placeholder="Nhựa PETG…" />
                </Field>
                <Field label="Công nghệ" hint="Gõ rồi Enter để thành thẻ." error={errors['specs.technologies']}>
                  <TagInput value={form.specs.technologies} onChange={(technologies) => patchSpecs({ technologies })} placeholder="In 3D, hoàn thiện tay…" />
                </Field>
                <Field label="Phụ kiện" hint="Gõ rồi Enter để thành thẻ." error={errors['specs.accessories']}>
                  <TagInput value={form.specs.accessories} onChange={(accessories) => patchSpecs({ accessories })} placeholder="Dây bi kim loại…" />
                </Field>
              </div>
            </Card>
          </div>
          <div className="space-y-5">
            <Card title="Hiển thị trên web">
              <div className="space-y-4">
                <Toggle checked={form.published} onChange={(v) => patch({ published: v })} label={form.published ? 'Đang hiển thị trên web' : 'Ẩn khỏi web (chỉ admin thấy)'} />
                <hr className="border-border" />
                <Toggle checked={form.featured} onChange={(v) => patch({ featured: v })} label="Nổi bật ở trang chủ" />
                <Toggle checked={form.isHot} onChange={(v) => patch({ isHot: v })} label="Sản phẩm HOT (mask + ưu tiên)" />
                <Toggle checked={form.isNewArrival} onChange={(v) => patch({ isNewArrival: v })} label="Sản phẩm MỚI (mask + ưu tiên)" />
                <Field label="Thứ tự tay" hint="Nhỏ lên trước, sau HOT/MỚI/nổi bật" error={errors.order}>
                  <Input type="number" min={0} value={form.order} onChange={(e) => patch({ order: Math.max(0, Math.floor(Number(e.target.value) || 0)) })} />
                </Field>
                <div>
                  <div className="mb-1.5 text-xs font-medium text-ink">Xem trước mask góc ảnh</div>
                  <div className="relative size-24 overflow-hidden rounded-image border border-border bg-sand">
                    {form.images[0] && <img src={form.images[0].sizes?.thumbnail?.url ?? form.images[0].url} alt="" className="size-full object-cover" />}
                    <BadgeMask product={previewBadgeInput} size="md" />
                    {!form.isHot && !form.isNewArrival && !onSalePreview && <span className="absolute inset-0 grid place-items-center text-2xs text-ink-faint">không có nhãn</span>}
                  </div>
                </div>
              </div>
            </Card>
            <Card title="Phân loại">
              <div className="space-y-4">
                <Field label="Danh mục" error={errors.category}><Select value={form.category} onChange={(e) => patch({ category: e.target.value })}><option value="">— Chưa phân loại —</option>{cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</Select></Field>
                <Field label="Màu có sẵn" hint="Chọn từ bảng màu dùng chung" error={errors.colors}>
                  <div className="flex flex-wrap gap-2">
                    {allColors.map((c) => { const on = form.colors.includes(c.id); return (
                      <button key={c.id} type="button" onClick={() => patch({ colors: on ? form.colors.filter((x) => x !== c.id) : [...form.colors, c.id] })} className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${on ? 'border-primary-700 bg-primary-50' : 'border-border-strong'}`}>
                        <span className="size-3.5 rounded-full border border-border" style={{ backgroundColor: c.hex }} />{c.name}
                      </button>) })}
                    {!allColors.length && <span className="text-xs text-ink-muted">Chưa có màu — thêm ở mục Màu sắc.</span>}
                  </div>
                </Field>
                <Field label="Tags" hint="Tối đa 30 tag, mỗi tag ≤ 40 ký tự" error={errors.tags}><TagInput value={form.tags} onChange={(tags) => patch({ tags })} placeholder="quà tặng, in 3d…" /></Field>
                <Field label="Thương hiệu" hint={`Trường Google khuyến nghị cho sản phẩm. Trống → dùng "${site?.defaultBrand || site?.siteName || 'tên shop'}".`} error={errors.brand}>
                  <Input value={form.brand} onChange={(e) => patch({ brand: e.target.value })} placeholder={site?.defaultBrand || site?.siteName || 'Nhà Nhỏ'} />
                </Field>
              </div>
            </Card>
            <Card title="Mua & tư vấn">
              <div className="space-y-4">
                <Field label="Link Shopee" hint='Nút "Mua" trên web dẫn ra đây' error={errors.shopeeUrl}><Input type="url" value={form.shopeeUrl} onChange={(e) => patch({ shopeeUrl: e.target.value })} placeholder="https://shopee.vn/…" /></Field>
                <Field label="Link Zalo" error={errors.zaloLink}><Input type="url" value={form.zaloLink} onChange={(e) => patch({ zaloLink: e.target.value })} placeholder="https://zalo.me/…" /></Field>
              </div>
            </Card>
          </div>
        </div>
      )}

      {tab === 'media' && (
        <div className="space-y-5">
          <Card title="Ảnh sản phẩm">
            <p className="mb-3 text-xs text-ink-muted">Kéo để đổi thứ tự. Ảnh đầu là ảnh đại diện (mask HOT/MỚI/-% sẽ nằm góc phải ảnh này trên web). {mediaSizeHint()} {errors.images && <span className="text-danger">{errors.images}</span>}</p>
            <SortableImages items={form.images} onChange={(images) => patch({ images })} onRemove={(mid) => patch({ images: form.images.filter((m) => m.id !== mid) })} />
            <Button variant="secondary" size="sm" className="mt-3" onClick={() => setPickImages(true)}>+ Thêm ảnh</Button>
            <MediaPicker open={pickImages} onClose={() => setPickImages(false)} multiple exclude={form.images.map((m) => m.id)} onPick={(docs) => patch({ images: [...form.images, ...docs] })} />
          </Card>
          <Card title="Video">
            {errors.videos && <p className="mb-2 text-xs text-danger">{errors.videos}</p>}
            {allVideos.length === 0 ? <p className="text-sm text-ink-muted">Chưa có video — thêm ở mục Video.</p> : (
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {allVideos.map((v) => { const on = form.videos.includes(v.id); return (
                  <label key={v.id} className={`flex cursor-pointer items-center gap-3 rounded-card-sm border p-2 ${on ? 'border-primary-700 bg-primary-50' : 'border-border'}`}>
                    <input type="checkbox" checked={on} onChange={() => patch({ videos: on ? form.videos.filter((x) => x !== v.id) : [...form.videos, v.id] })} />
                    <div className="h-10 w-16 shrink-0 overflow-hidden rounded bg-sand">{v.thumbnailUrl && <img src={v.thumbnailUrl} alt="" className="size-full object-cover" />}</div>
                    <span className="min-w-0 truncate text-sm">{v.title}</span>
                  </label>) })}
              </div>
            )}
          </Card>
        </div>
      )}

      {tab === 'variants' && (
        <Card title="Biến thể (SKU · giá · tồn kho riêng)">
          {errors.variants && <p className="mb-2 text-xs text-danger">{errors.variants}</p>}
          <VariantsEditor rows={form.variants} onChange={(variants) => patch({ variants })} colors={selectedColors.length ? selectedColors : allColors} basePrice={form.price} errors={errors} discountPercent={salePercent} />
          {form.variants.length === 0 && <Button variant="secondary" size="sm" className="mt-3" onClick={() => patch({ variants: [newVariant(form.price)] })}>+ Biến thể đầu tiên</Button>}
        </Card>
      )}

      {tab === 'seo' && (
        <div className="space-y-5">
          <Card>
            <SeoPanel
              kind="product"
              value={form.seo}
              onChange={(seo) => patch({ seo })}
              pageTitle={form.title}
              slug={`san-pham/${form.slug}`}
              html={form.description}
              errors={errors}
              settings={site}
              docId={id}
              extra={{
                attachedImages: form.images.map((m) => ({ alt: m.alt })),
                price: form.price,
                hasSku: !!form.sku.trim(),
                hasCategory: !!form.category,
                brand: form.brand || site?.defaultBrand || null,
                hasVideo: form.videos.length > 0,
                faqCount: form.faq.filter((f) => f.question.trim() && f.answer.trim()).length,
              }}
            />
          </Card>
          <Card title="Câu hỏi thường gặp (FAQ trên Google)">
            <FaqEditor items={form.faq} onChange={(faq) => patch({ faq })} errors={errors} />
          </Card>
        </div>
      )}

      <ConfirmDialog open={del} onCancel={() => setDel(false)} onConfirm={async () => {
        if (!id) return
        setBusy(true)
        try { await api.remove(id); toast.success('Đã xoá'); setInitial(form); navigate('/products') } catch (e) { toast.error(explainError(e).message) } finally { setBusy(false) }
      }} loading={busy} title="Xoá sản phẩm" message={<>Xoá <strong>{form.title}</strong>? Không khôi phục được.</>} />

      <ConfirmDialog open={confirmHide} onCancel={() => setConfirmHide(false)} confirmLabel="Gỡ khỏi web" loading={busy}
        onConfirm={() => { setConfirmHide(false); save({ ...form, published: false }) }}
        title="Gỡ sản phẩm khỏi web?" message={<>Khách sẽ không thấy <strong>{form.title}</strong> trên web nữa; link cũ trả 404. Dữ liệu vẫn giữ trong admin, bật lại lúc nào cũng được.</>} />

      <ConfirmDialog open={confirmSlug !== null} onCancel={() => setConfirmSlug(null)} confirmLabel="Vẫn đổi slug" loading={busy}
        onConfirm={() => { const fn = confirmSlug; setConfirmSlug(null); fn?.() }}
        title="Đổi slug của sản phẩm đang hiển thị?" message={<>Link cũ <code>/san-pham/{initial?.slug}</code> sẽ được <strong>tự động chuyển 301</strong> sang địa chỉ mới, nên không ai gặp 404. Nhưng Google vẫn cần thời gian chuyển thứ hạng sang URL mới. Nếu chỉ sửa tên sản phẩm thì <strong>giữ slug</strong>.</>} />
    </>
  )
}
